import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { RefreshCw, Play } from 'lucide-react';
import { BettingTable } from '../components/BettingTable';
import { ChipSelector } from '../components/ChipSelector';
import { useRoulette } from '../hooks/useRoulette';
import { ResultModal } from '../components/ResultModal';
import { RouletteWheel } from '../components/RouletteWheel';

export const RouletteSection = () => {
  const {
    balance,
    currentBets,
    isSpinning,
    lastWinningNumber,
    history,
    placeBet,
    clearBets,
    spinWheel,
    selectedChip,
    setSelectedChip,
    resetBalance,
    totalBetAmount,
    showModal,
    setShowModal,
    winningBets,
    totalWon
  } = useRoulette();

  return (
    <section id="roulette" className="py-24 bg-[#0a0c10] relative">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">

        {/* Top Header Row for Controls */}
        <div className="flex flex-col lg:flex-row justify-between items-start lg:items-end mb-8 gap-6">
          <h2 className="text-4xl md:text-5xl font-serif text-white tracking-wide">
            Live Roulette <span className="text-gold-500">Table</span>
          </h2>

          <div className="flex items-center bg-[#13151b] rounded-xl border border-gray-800 p-4 shadow-lg min-w-[280px]">
            <div className="flex-1 px-4">
              <p className="text-[10px] text-gray-500 uppercase font-bold tracking-wider mb-1">DEMO BALANCE</p>
              <p className="text-2xl font-bold text-white">${balance}</p>
            </div>
            <div className="w-[1px] h-10 bg-gray-700 mx-2" />
            <button
              onClick={resetBalance}
              className="flex items-center gap-2 text-gray-400 hover:text-white px-4 transition-colors text-sm font-semibold"
            >
              <RefreshCw size={16} />
              RESET
            </button>
          </div>
        </div>

        <div className="flex flex-col lg:flex-row gap-8">

          {/* Left Column - Wheel & Controls */}
          <div className="w-full lg:w-1/2 flex flex-col gap-6">

            {/* Spin Panel */}
            <div className="bg-gradient-to-b from-[#1b202e] to-[#0d1017] rounded-3xl border border-gray-800 p-8 shadow-2xl flex flex-col items-center justify-center relative">
              <div className="w-full aspect-square max-w-[320px] lg:max-w-[400px] mb-8 relative">
                <RouletteWheel isSpinning={isSpinning} lastWinningNumber={lastWinningNumber} isAmbient={lastWinningNumber === null} />
              </div>

              <button
                onClick={spinWheel}
                disabled={isSpinning || totalBetAmount === 0}
                className={`
                    w-full py-4 rounded-full font-bold text-lg tracking-widest uppercase transition-all
                    flex items-center justify-center gap-3
                    ${totalBetAmount > 0 && !isSpinning
                    ? 'bg-gradient-to-r from-[#d1a638] to-[#9c7923] text-black shadow-[0_0_20px_rgba(212,175,55,0.2)] hover:shadow-[0_0_30px_rgba(212,175,55,0.4)]'
                    : 'bg-gradient-to-r from-[#d1a638]/50 to-[#9c7923]/50 text-black/50 cursor-not-allowed shadow-none'}
                  `}
              >
                <RefreshCw className={`w-5 h-5 ${isSpinning ? 'animate-spin' : ''}`} />
                SPIN WHEEL
              </button>

              <p className="text-gray-400 text-sm mt-4 font-medium">
                {totalBetAmount > 0 ? `Total Bet: $${totalBetAmount}` : 'Press Spin Wheel to see a demo result.'}
              </p>
            </div>

            {/* Current Selection Panel */}
            <div className="bg-[#13151b] rounded-2xl border border-gray-800 p-6 shadow-xl relative min-h-[160px]">
              <p className="text-xs text-gray-500 font-bold uppercase tracking-wider mb-2">CURRENT SELECTION</p>

              <h3 className="text-xl font-serif text-gold-500 mb-4 uppercase">
                {totalBetAmount > 0 ? `${currentBets.length} Bets Placed` : 'NO SELECTION'}
              </h3>

              <p className="text-sm text-gray-400">
                Virtual chip placed: <span className="text-gold-500 font-bold">${selectedChip}</span> — demo credits with no monetary value.
              </p>

              {currentBets.length > 0 && (
                <div className="absolute bottom-6 right-6">
                  <button
                    onClick={clearBets}
                    disabled={isSpinning}
                    className="text-xs font-bold text-gray-500 hover:text-gray-300 uppercase tracking-widest transition-colors"
                  >
                    CLEAR BETS
                  </button>
                </div>
              )}
            </div>

            {/* Chip Selector */}
            <div className="mt-2 mb-4">
              <ChipSelector
                selectedChip={selectedChip}
                onSelectChip={setSelectedChip}
                isSpinning={isSpinning}
              />
            </div>

          </div>

          {/* Right Column - Betting Table */}
          <div className="w-full lg:w-1/2 overflow-x-auto scrollbar-hide pb-4 flex">
            <div className="min-w-[650px] w-full bg-[#123d26] rounded-2xl p-6 md:p-8 shadow-2xl border border-[#1a5736] flex items-center justify-center">
              <BettingTable
                onPlaceBet={(type) => placeBet(type, selectedChip)}
                currentBets={currentBets}
                isSpinning={isSpinning}
              />
            </div>
          </div>

        </div>

      </div>

      {/* Results Modal */}
      <ResultModal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        winningNumber={lastWinningNumber}
        winningBets={winningBets}
        totalWon={totalWon}
      />
    </section>
  );
};
